import {
  canApplyIncrementally,
  defaultFeatures,
  initialState,
  localHour as localHourOf,
  reduce,
  replay,
  defaultRuleset,
  studyDate,
  syncProgression,
} from '@aj/core';
import type {
  AppEvent,
  CommandContext,
  CommandResult,
  DerivedState,
  FeatureFlags,
  Ruleset,
  StudyDate,
} from '@aj/core';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';

import { webPorts } from '../adapters/ports.js';
import { appendEvents, readMeta, writeMeta } from './db.js';
import { useEventLog } from './useEventLog.js';

/**
 * THE BRIDGE.
 *
 *   UI ──action──▶ command (pure) ──events──▶ IndexedDB
 *                                               │ delta
 *                                               ▼
 *                            reduce (incremental) ──▶ DerivedState ──▶ UI
 *
 * Everything on that path is offline and synchronous-feeling. No network on
 * any daily interaction — §26 held literally.
 *
 * Screens never import `reduce`, `db`, or the command functions directly.
 * They call `actions.*` and read selectors, so the storage engine and the
 * state shape stay replaceable.
 */

type AppData = {
  state: DerivedState;
  /** Current study day under the 4am rollover. Refreshes on a timer so an
   *  app left open overnight rolls over on its own. */
  today: StudyDate;
  localHour: number;
  features: FeatureFlags;
  ruleset: Ruleset;
  /** False only during the first load from IndexedDB. */
  ready: boolean;
  /** Storage is unusable. Screens must say so rather than render nothing. */
  error: Error | null;
  actions: Actions;
};

/**
 * Every write the UI can perform. Thin wrappers over @aj/core commands —
 * they add persistence and progression bookkeeping, and nothing else.
 */
export type Actions = {
  run(command: (state: DerivedState, ctx: CommandContext) => CommandResult): Promise<CommandResult>;
};

const AppDataContext = createContext<AppData | null>(null);

/** Re-render on an interval so `today` and `localHour` stay honest without
 *  every consumer reaching for a clock. */
function useTick(intervalMs: number): number {
  const [now, setNow] = useState(() => webPorts.clock.now());
  useEffect(() => {
    const id = setInterval(() => setNow(webPorts.clock.now()), intervalMs);
    return () => clearInterval(id);
  }, [intervalMs]);
  return now;
}

export function AppDataProvider({
  children,
  features = defaultFeatures,
  ruleset = defaultRuleset,
}: {
  children: ReactNode;
  features?: FeatureFlags;
  ruleset?: Ruleset;
}) {
  const now = useTick(60_000);
  const offset = webPorts.clock.utcOffsetMinutes(now);
  const today = studyDate(now, offset);
  const localHour = localHourOf(now, offset);

  /**
   * The checkpoint. Kept in a ref so the reducer can apply incrementally
   * rather than replaying the whole log on every keystroke — full replay only
   * happens on a ruleset bump or an out-of-order merge.
   */
  const checkpoint = useRef<DerivedState>(initialState(ruleset.version));
  const [restored, setRestored] = useState(false);

  useEffect(() => {
    let cancelled = false;
    readMeta('checkpoint').then(
      (saved) => {
        if (cancelled) return;
        // A checkpoint built by a different ruleset is worthless — drop it and
        // let the log rebuild. That is the whole point of versioning rules.
        if (saved && saved.rulesetVersion === ruleset.version) checkpoint.current = saved;
        setRestored(true);
      },
      () => {
        // The checkpoint is only a cache. If it can't be read, carry on from
        // empty and let the log rebuild — never block the screen on it. A
        // genuinely broken database surfaces through the event log instead.
        if (!cancelled) setRestored(true);
      },
    );
    return () => {
      cancelled = true;
    };
  }, [ruleset.version]);

  const { events, error: storageError } = useEventLog();

  const state = useMemo(() => {
    if (!events) return checkpoint.current;

    const previous = checkpoint.current;
    const fresh = events.filter((event) => previous.cursor === null || event.id > previous.cursor);

    const next =
      fresh.length === events.length || !canApplyIncrementally(previous, fresh, ruleset)
        ? replay(events, ruleset)
        : reduce(previous, fresh, ruleset);

    checkpoint.current = next;
    return next;
  }, [events, ruleset]);

  // Persist the checkpoint lazily. It is a cache, so losing it costs a replay
  // and nothing else — no reason to block a tap on writing it.
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (!restored) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => void writeMeta('checkpoint', state), 1_500);
    return () => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
    };
  }, [state, restored]);

  /** Latest state, readable from a stable callback. */
  const latest = useRef(state);
  latest.current = state;

  const run = useCallback<Actions['run']>(
    async (command) => {
      const clockNow = webPorts.clock.now();
      const tz = webPorts.clock.utcOffsetMinutes(clockNow);
      const ctx: CommandContext = {
        now: clockNow,
        tzOffsetMinutes: tz,
        today: studyDate(clockNow, tz),
        newId: () => webPorts.id.ulid(clockNow),
        source: 'pwa',
      };

      const result = command(latest.current, ctx);
      if (!result.ok) return result;

      await appendEvents(result.events);

      // Achievements and rank-ups are detected by folding but stored as
      // facts, so they survive a future ruleset that would not grant them.
      const projected = reduce(latest.current, result.events, ruleset);
      const earned: AppEvent[] = syncProgression(projected, ctx, ruleset);
      if (earned.length > 0) await appendEvents(earned);

      return { ok: true, events: [...result.events, ...earned] };
    },
    [ruleset],
  );

  const value = useMemo<AppData>(
    () => ({
      state,
      today,
      localHour,
      features,
      ruleset,
      ready: restored && events !== undefined,
      error: storageError,
      actions: { run },
    }),
    [state, today, localHour, features, ruleset, restored, events, storageError, run],
  );

  return <AppDataContext.Provider value={value}>{children}</AppDataContext.Provider>;
}

export function useAppData(): AppData {
  const ctx = useContext(AppDataContext);
  if (!ctx) throw new Error('useAppData must be used inside AppDataProvider');
  return ctx;
}
